import { ClipboardDocumentIcon, EyeIcon } from "@heroicons/react/24/outline";
import { ICustomer } from "../../models/Customer/Customer";
import getAddress from "../../utils/getAddress";
import { Button } from "../base/Button";
import Card from "../shared/Card/Card";
import { useClipboardCopy } from "../../hooks/useClipboardCopy";
import { Fragment, useState } from "react";

interface BasicInformationProps {
  customer: ICustomer;
}

export function BasicInformation({ customer }: BasicInformationProps) {
  const { CopyToClipboardButton } = useClipboardCopy();
  const [showKey, setShowKey] = useState(false);

  return (
    <Card title="Basic Information">
      <dl className="px-4 divide-y divide-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-3 py-2">
          <dt className="text-sm font-medium leading-6 text-gray-900">ID</dt>
          <dd className="mt-1 text-sm leading-6 text-gray-700 md:col-span-2 md:mt-0">
            <div className="flex items-center justify-between">
              <span>{customer.id}</span>
              <CopyToClipboardButton copyText={customer.id} />
            </div>
          </dd>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 py-2">
          <dt className="text-sm font-medium leading-6 text-gray-900">Name</dt>
          <dd className="mt-1 text-sm leading-6 text-gray-700 md:col-span-2 md:mt-0">
            {customer.name}
          </dd>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 py-2">
          <dt className="text-sm font-medium leading-6 text-gray-900">Username Prefix</dt>
          <dd className="mt-1 text-sm leading-6 text-gray-700 md:col-span-2 md:mt-0">
            {customer.usernamePrefix}
          </dd>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 py-2">
          <dt className="text-sm font-medium leading-6 text-gray-900">
            Domain
          </dt>
          <dd className="mt-1 text-sm leading-6 text-gray-700 md:col-span-2 md:mt-0">
            {customer.domain}
          </dd>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 py-2">
          <dt className="text-sm font-medium leading-6 text-gray-900">
            Front-End Domain
          </dt>
          <dd className="mt-1 text-sm leading-6 text-gray-700 md:col-span-2 md:mt-0">
            {customer.frontEndDomain}
          </dd>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 py-2">
          <dt className="text-sm font-medium leading-6 text-gray-900">
            Address
          </dt>
          <dd className="mt-1 text-sm leading-6 text-gray-700 md:col-span-2 md:mt-0">
            {getAddress({
              line1: customer.line1,
              line2: customer.line2,
              line3: customer.line3,
              line4: customer.line4,
              locality: customer.locality,
              town: customer.town,
              county: customer.county,
              postcode: customer.postcode,
            })
              .split(",")
              .map((addressLine, i) => (
                <Fragment key={i}>
                  <span>{addressLine.trim()}</span>
                  <br />
                </Fragment>
              ))}
          </dd>
        </div><div className="grid grid-cols-1 md:grid-cols-3 py-2">
          <dt className="text-sm font-medium leading-6 text-gray-900">
            Telephone
          </dt>
          <dd className="mt-1 text-sm leading-6 text-gray-700 md:col-span-2 md:mt-0">
            {customer.telephone}
          </dd>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 py-2">
          <dt className="text-sm font-medium leading-6 text-gray-900">Email</dt>
          <dd className="mt-1 text-sm leading-6 text-gray-700 md:col-span-2 md:mt-0">
            {customer.email}
          </dd>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 py-2">
          <dt className="text-sm font-medium leading-6 text-gray-900">Contact Name</dt>
          <dd className="mt-1 text-sm leading-6 text-gray-700 md:col-span-2 md:mt-0">
            {customer.contactName}
          </dd>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 py-2">
          <dt className="text-sm font-medium leading-6 text-gray-900">SMTP From</dt>
          <dd className="mt-1 text-sm leading-6 text-gray-700 md:col-span-2 md:mt-0">
            {customer.smtpFrom}
          </dd>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 py-2">
          <dt className="text-sm font-medium leading-6 text-gray-900">SMTP From Name</dt>
          <dd className="mt-1 text-sm leading-6 text-gray-700 md:col-span-2 md:mt-0">
            {customer.smtpFromName}
          </dd>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 py-2">
          <dt className="text-sm font-medium leading-6 text-gray-900">
            Signing Key
          </dt>
          <dd className="mt-1 text-sm leading-6 text-gray-700 md:col-span-2 md:mt-0">
            {showKey ? (
              <div className="flex space-x-2">
                <p className="flex-grow text-wrap break-all">
                  {customer.signingKey}
                </p>
                <CopyToClipboardButton copyText={customer.signingKey} />
              </div>
            ) : (
              <Button
                fullWidth
                onClick={() => setShowKey(true)}
                size="sm"
                className="flex items-center justify-center space-x-2"
              >
                <EyeIcon className="w-5 h-5" />
                <p>Show</p>
              </Button>
            )}
          </dd>
        </div>
      </dl>
    </Card>
  );
}
